import { computed } from 'vue'
import {
  DATE_FORMAT,
  FIRST_DAY_OF_WEEK,
  NUMBER_FORMAT,
  WEEK_NUMBER_FORMAT,
  type DateFormat,
  type FirstDayOfWeek,
  type NumberFormat
} from '@gat-ship/types'
import { useUser } from '@gat-ship/user'
import {
  dateFormatOptions,
  findOptionTitle,
  firstDayOfWeekOptions,
  numberFormatOptions,
  weekNumberFormatOptions
} from '../model/localization-options'

/** The non-standard `weekInfo` carried by the resolved browser locale. */
type LocaleWithWeekInfo = Intl.Locale & {
  weekInfo?: { weekend?: number; firstDay?: number }
}

/**
 * Localization-panel seam: builds the format-option dropdowns from the resolved
 * browser defaults (the only impure read, done here) + the configured format
 * enums, and owns the preference setters. The dedup logic itself is pure.
 */
export function useLocalizationSettings() {
  const { userPreferences } = useUser()

  const locale = computed(
    () => userPreferences.systemSettings.value.locale as LocaleWithWeekInfo
  )

  const dateOptions = computed(() =>
    dateFormatOptions(locale.value.baseName, DATE_FORMAT)
  )

  const weekNumberOptions = computed(() =>
    weekNumberFormatOptions(
      locale.value.weekInfo?.weekend?.toString(),
      WEEK_NUMBER_FORMAT
    )
  )

  const firstDayOptions = computed(() =>
    firstDayOfWeekOptions(
      locale.value.weekInfo?.firstDay?.toString(),
      FIRST_DAY_OF_WEEK
    )
  )

  const numberOptions = computed(() =>
    numberFormatOptions(locale.value.baseName, NUMBER_FORMAT)
  )

  function setDateFormat(value: unknown) {
    userPreferences.updateUserPreferences({ dateFormat: value as DateFormat })
  }

  function setWeekNumberFormat(value: unknown) {
    userPreferences.updateUserPreferences({ weekNumberFormat: value as string })
  }

  function setFirstDayOfWeek(value: unknown) {
    userPreferences.updateUserPreferences({
      firstDayOfWeek: value as FirstDayOfWeek
    })
  }

  function setNumberFormat(value: unknown) {
    userPreferences.updateUserPreferences({
      numberFormat: value as NumberFormat
    })
  }

  return {
    dateOptions,
    weekNumberOptions,
    firstDayOptions,
    numberOptions,
    findTitle: findOptionTitle,
    setDateFormat,
    setWeekNumberFormat,
    setFirstDayOfWeek,
    setNumberFormat
  }
}
